import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ConnectorService } from '../connector.service';

export interface Campaign {
  id: number;
  name: string;
  slug: string;
  description: string;
  description_html: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  choices: CampaignChoice[];
  delivery_options: DeliveryOption[];
  stock_groups?: CampaignStockGroup[];
  existingUserChoices?: any[];
}

export interface CampaignStockGroup {
  id: number;
  name: string;
  quantity_limit: number;
  quantity_sold: number;
  remaining_quantity: number | null;
}

export interface CampaignChoice {
  id: number;
  name: string;
  description: string;
  price: number;
  is_visible: boolean;
  requires_delivery: boolean;
  grants_adherent?: boolean;
  grants_socio?: boolean;
  grants_wall_message?: boolean;
  visibility_condition?: 'everybody' | 'no_account' | 'adherent_only' | 'socio_only' | 'adherent_socio';
  quantity_limit?: number | null;
  quantity_sold?: number;
  remaining_quantity?: number | null;
  is_available?: boolean;
  stock_group_id?: number | null;
}

export interface DeliveryOption {
  id: number;
  name: string;
  description: string;
  price: number;
  is_visible: boolean;
}

export interface Profile {
  id: number;
  fullName: string;
  selectedOption?: number | null;
  selectedOptions?: number[];
  socio?: any; // Référence au socio pour les informations supplémentaires
  plaqueType?: 'full' | 'simple' | 'none'; // Type de brique déjà acquise sur le mur, si connu
}

export interface CampaignResponse {
  campaign?: Campaign;
  profiles?: Profile[];
  options?: CampaignChoice[];
  existingUserChoices?: any[];
  // Propriétés directes de la campagne (pour l'endpoint public)
  id?: number;
  name?: string;
  slug?: string;
  description?: string;
  description_html?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  choices?: CampaignChoice[];
  delivery_options?: DeliveryOption[];
  stock_groups?: CampaignStockGroup[];
}

@Injectable({
  providedIn: 'root'
})
export class CampaignService {
  private apiUrl = environment.backAPI;

  constructor(
    private http: HttpClient,
    private connector: ConnectorService
  ) {}

  /** reload=true : rechargement de la même page (ex. après connexion), non compté comme une nouvelle vue. */
  getCampaign(slug: string, reload: boolean = false): Observable<CampaignResponse> {
    // Préparer les headers avec le token d'authentification si l'utilisateur est connecté
    const headers: any = {
      'Content-Type': 'application/json'
    };
    
    if (this.connector.isConnected()) {
      headers['Authorization'] = 'Bearer ' + this.connector.token();
    }
    
    const query = reload ? '?reload=1' : '';
    return this.http.get<CampaignResponse>(`${this.apiUrl}/public-campaigns/${slug}/with-user-status${query}`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  getPublicCampaigns(): Observable<Campaign[]> {
    return this.http.get<Campaign[]>(`${this.apiUrl}/public-campaigns`)
      .pipe(
        catchError(this.handleError)
      );
  }

  getCampaignLink(slug: string): string {
    return `/c/${slug}`;
  }

  submitSelection(slug: string, data: any, isNewAccount: boolean = false): Observable<any> {
    const endpoint = isNewAccount ? 'submit-new-account' : 'submit';
    
    // Headers avec authentification pour les comptes existants
    const headers: any = {
      'Content-Type': 'application/json'
    };
    
    // Ajouter le token d'authentification seulement pour les comptes existants
    if (!isNewAccount) {
      headers['Authorization'] = 'Bearer ' + this.connector.token();
    }
    
    return this.http.post(`${this.apiUrl}/public-campaigns/${slug}/${endpoint}`, data, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Une erreur est survenue';
    
    if (error.error instanceof ErrorEvent) {
      // Erreur côté client
      errorMessage = `Erreur: ${error.error.message}`;
    } else {
      // Erreur côté serveur
      if (error.error?.message) {
        // Message d'erreur personnalisé du backend (BadRequestException)
        errorMessage = error.error.message;
      } else if (error.error?.error) {
        // Format alternatif pour les erreurs NestJS
        errorMessage = error.error.error;
      } else if (typeof error.error === 'string') {
        // Message d'erreur direct
        errorMessage = error.error;
      } else {
        // Message générique avec code de statut
        const statusText = error.statusText || 'Erreur serveur';
        errorMessage = `${statusText} (${error.status})`;
      }
    }
    
    console.error('Campaign service error:', error);
    return throwError(() => new Error(errorMessage));
  }
} 